import GPT from '@op/components/GPT';
import React, { useMemo, useState } from 'react';
import { Form, notification } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import { useReportPermissionNotify } from '@op/components/ReportPermission';
import { KeywordsOptions } from './components/KeywordOptions';
import UrlOptions from './components/UrlOptions';
import { useFilterUrlList } from '../../../hook';
import GptOptions from './components/GptOptions';
import { KeywordUrlFilterProps } from '../../../types';
import { createUrlDrillTask } from '@op/services';
import { FilterKeys } from '@op/components/FilterCommon/types';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import ResultsOutput from './components/ResultsOutput';
import TimingModal from './components/TimingModal';
import CommonFormButtons from './components/CommonFormButtons';
import { useCommonFilter } from './hooks/useCommonFilter';
import { useFilterConfig } from './hooks/useFilterConfig';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';
import { FilterFixed } from '@op/components/FilterCommon';

export default function UrlFilter({
  gameInfo,
  promptList,
  setLoading,
  loading,
  changeFilterValue,
  resetSaveId,
  wholeGameInfo,
  videoPromptList,
  tab,
  userInfo,
  setTranslateTopicMap,
}: KeywordUrlFilterProps) {
  const { formatMessage } = useLanguage();
  const permissionNotify = useReportPermissionNotify();

  // 使用共同的 hook
  const {
    emailList,
    setShowEmailMessage,
    saveName,
    setSaveName,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
    filterOnChange,
    promptInfo,
    setPromptInfo,
    videoPromptInfo,
    commentsPromptInfo,
    keywordPromptInfo,
    visible,
    toggleVisible,
    initPromptInfo,
    modalTitle,
    gptType,
    setGptType,
    remainder,
    total,
    advancedLimitLoading,
    getPromptInfo,
    resetPromptInfo,
    gptList,
    setGptList,
    realPromptList,
    isAdvanced,
    getPromptRealDetail,
    keywordCustomInput,
    setKeywordCustomInput,
    keywordSearchType,
    setKeywordSearchType,
    grammarTrue,
    grammarText,
    initCustomInputValue,
    getKeywordInfo,
    createShortUrl,
  } = useCommonFilter({
    tab,
    wholeGameInfo,
    gameInfo,
    promptList,
    videoPromptList,
    userInfo,
    analysisSuffix: 'URL Analysis',
  });

  // url 筛选器参数
  const {
    urlList,
    setUrlList,
    getUrlFilter,
    urlDisabled,
    resetUrlList,
    hasUrlList,
    checkUrlListFunc,
    loading: urlLoading,
    initUrlList,
  } = useFilterUrlList();

  const {
    scrollBody,
    filterLoading,
    changeFilter,
    filterOptions,
    confirmRef,
    filterReset,
    filterSave,
    filterConfirm,
    confirmDisabled,
    saveDisabled,
    setSaveDisabled,
    isGenerated,
    setDisabled,
    defaultTime,
  } = useFilterConfig({
    gameInfo,
    tab,
    grammarTrue,
    urlDisabled,
    hasUrlList,
  });

  const renderExcludeFilterMemo = useMemo((): Array<FilterKeys> => {
    return ['monitor'];
  }, []);

  function getOtherInfo(isCreateShortid = false, filterPrompt = false) {
    const obj: any = { ...getKeywordInfo(isCreateShortid) };

    if (isCreateShortid) {
      obj.promptInfo = getPromptInfo(filterPrompt);
      obj.gptList = gptList;
    } else {
      // comments: 1 , video: 2 , 全选: 3
      const needSummary: number[] = [];
      gptList.map((item) => {
        if (item === 'comments') {
          const data = getPromptRealDetail(commentsPromptInfo);
          obj.comments_task_type = data?.gptOptions;
          obj.comments_prompt = data?.prompt;
          needSummary.push(1);
        } else {
          const data = getPromptRealDetail(videoPromptInfo);
          obj.video_task_type = data?.gptOptions;
          obj.video_prompt = data?.prompt;
          needSummary.push(2);
        }
      });
      let needSummaryNumber = 0;
      if (needSummary.length === 2) {
        needSummaryNumber = 3;
      } else if (needSummary.length === 1) {
        [needSummaryNumber] = needSummary;
      }
      obj.need_summary = needSummaryNumber;
    }

    // 如果是url参数，就加上url
    obj.content_urls = getUrlFilter();
    obj.model_type = gptType;
    return obj;
  }

  async function urlConfirm(filter: any, saveFilter: any) {
    // URL的情况下去掉官号筛选器
    const { social_media_by_account, social_media_by_keyword, ...otherFilter } =
      filter;
    const {
      social_media_by_account: a,
      social_media_by_keyword: b,
      ...otherSaveFilter
    } = saveFilter;

    const short_url = await createShortUrl(
      otherSaveFilter,
      formatMessage('Analysis Generate failed, please try again'),
    );

    if (!short_url) return;
    const params = { ...otherFilter, short_url };

    if (isSave) {
      if (saveName.trim()) {
        params.should_save_report = true;
        params.save_report_name = saveName;
      }

      if (emailList.length > 0) {
        params.email = emailList.join(',');
      }
    }

    // url保存的时候data.id需要变成string类型，在修改的时候，需要变为number类型
    const promptInfo = getPromptInfo();
    createUrlDrillTask(params)
      .then((data) => {
        // 暂存权限目标，待内容区滚动到位后由 useReportCreateSuccess 弹出权限提示
        if (params.should_save_report) {
          permissionNotify?.setPendingReportTarget(data);
        }
        changeFilterValue({
          filter: otherFilter,
          oriFilter: otherSaveFilter,
          tab,
          pageFilter: {
            task_id: String(data.id),
            short_url,
            category: tab,
            ...gameInfo,
          },
          updateFilter: { lead_id: data.id },
          promptInfo,
          gptList,
          urlTaskId: data.id,
          isSave,
        });
      })
      .catch((error) => {
        if (error.code === 40003) {
          notification.error({
            description: formatMessage({
              id: 'Unable to scrape subtitles from this URL',
            }),
          });
        } else if (error.code === 40004) {
          notification.error({
            description: formatMessage({
              id: 'Unable to scrape the live stream comment from this URL',
            }),
          });
        } else {
          notification.error({
            description: formatMessage({
              id: '系统错误',
            }),
          });
        }

        changeFilterValue({
          filter: null,
          oriFilter: null,
          pageFilter: null,
          tab,
          isSave,
        });
      });
  }

  async function confirm(filterData: any, oriFilterValue: any) {
    setLoading(true);
    const filter = { ...getOtherInfo(), ...filterData };
    const saveFilter = { ...getOtherInfo(true), ...oriFilterValue };

    const confirmFunc = urlConfirm;
    try {
      await confirmFunc(filter, saveFilter);
    } catch (error) { }

    const operationLog = { ...getOtherInfo(true, true), ...oriFilterValue };

    buriedBtnPoint(
      'GenerateReport',
      operationLog,
      operationLog?.keywordCustomInput,
      `${operationLog?.content_urls}`,
    );
    setLoading(false);
  }

  const [urlTipsVisible, setUrlTipsVisible] = useState(false);
  const [urlTipsParams, setUrlTipsParams] = useState({});

  async function generateBtnClick() {
    const flag = await checkUrlListFunc();

    if (!flag) return;
    isGenerated.current = true;
    resetSaveId();
    setTimeout(() => {
      filterConfirm(true);
    }, 500);
  }

  async function saveBtnClick() {
    const flag = await checkUrlListFunc();
    if (!flag) return;
    filterSave();
  }

  return (
    <div className="keyword-url-filter-wrapper">
      <FilterFixed
        filter={filterOptions}
        confirmRef={confirmRef}
        scrollBody={scrollBody}
        keyOperateShowOptions={[]}
        operateShowOptions={['more', 'history']}
        beforeCacheDealwithData={() => false}
        onlySaveFilters={['keyWords', 'urlAnalysis']}
        renderExcludeFilter={renderExcludeFilterMemo}
        defaultConfig={{ date: { permanentTipsKey: 'urlCommon' } }}
        onChange={(value: any) => {
          filterOnChange(value);
        }}
        beforeSaveFilterSetting={(data) => {
          const { value, fixedHeader } = data;
          const urlList = getUrlFilter();
          value.keywordActiveId = 'custom';
          value.keywordCustomInput = keywordCustomInput;
          value.keywordSearchType = keywordSearchType;
          value.content_urls = urlList;
          fixedHeader.keywordActiveId = 'custom';
          fixedHeader.keywordCustomInput = keywordCustomInput;
          fixedHeader.keywordSearchType = keywordSearchType;
          fixedHeader.content_urls = urlList;
          return data;
        }}
        historyFilterChange={(_name: string, data: any) => {
          initCustomInputValue(data);
        }}
        finallyDealwithDefaultValue={(
          data: any,
          _otherPageParams?: any,
          isDefaultValue?: boolean,
        ) => {
          if (!isDefaultValue) {
            initCustomInputValue(data);
            initPromptInfo(data);
            initUrlList(data);
          }
          return { ...data, ...defaultTime };
        }}
        toggleSaveBtnDisable={(value) => setSaveDisabled(value)}
        confirm={(value: any, oriFilterValue: any) => {
          const filterData = { ...value, ...gameInfo };
          changeFilter(filterData);

          function commonConfirm() {
            confirm(filterData, oriFilterValue);
            isGenerated.current = false;
          }

          if (isGenerated.current) {
            setUrlTipsParams({
              filter: filterData,
              onClick: () => {
                commonConfirm();
                setUrlTipsVisible(false);
              },
              onClose: () => setUrlTipsVisible(false),
            });
            setUrlTipsVisible(true);
          }
        }}
        backCondOriginData={(data) => {
          setTranslateTopicMap?.(data?.topic_trans || []);
        }}
        confirmDisabledOnChange={(value: boolean) => setDisabled(value)}
      />

      <CardCommon
        loading={filterLoading}
        loadingHeight={240}
        className="dbo-keywords-analysis-form"
      >
        <Form>
          <UrlOptions
            tab={tab}
            urlList={urlList}
            setUrlList={setUrlList}
          ></UrlOptions>
          <KeywordsOptions
            gameInfo={gameInfo}
            keywordSearchType={keywordSearchType}
            keywordCustomInput={keywordCustomInput}
            setKeywordSearchType={setKeywordSearchType}
            grammarText={grammarText}
            grammarTrue={grammarTrue}
            setKeywordCustomInput={setKeywordCustomInput}
            tab={tab}
          ></KeywordsOptions>
          <GptOptions
            remainder={remainder}
            total={total}
            gptType={gptType}
            setGptType={setGptType}
            toggleVisible={toggleVisible}
            setPromptInfo={setPromptInfo}
            videoPromptInfo={videoPromptInfo}
            commentsPromptInfo={commentsPromptInfo}
            keywordPromptInfo={keywordPromptInfo}
            advancedLimitLoading={advancedLimitLoading}
            gptList={gptList}
            setGptList={setGptList}
            tab={tab}
          ></GptOptions>
          <ResultsOutput
            name={saveName}
            setName={setSaveName}
            isSave={isSave}
            setIsSave={setIsSave}
            emailList={emailList}
            setEmailList={setEmailList}
            setShowEmailMessage={setShowEmailMessage}
            showEmailMessage={showEmailMessage}
            gameInfo={gameInfo}
          ></ResultsOutput>

          <CommonFormButtons
            gameInfo={gameInfo}
            loading={loading}
            extraLoading={urlLoading}
            confirmDisabled={!!confirmDisabled}
            saveDisabled={saveDisabled}
            saveName={saveName}
            showEmailMessage={showEmailMessage}
            isSave={isSave}
            onGenerate={() => {
              generateBtnClick();
            }}
            onReset={() => {
              filterReset();
              initCustomInputValue({});
              resetUrlList();
              resetPromptInfo();
            }}
            tabType="url"
            onSave={void saveBtnClick}
          />
        </Form>
      </CardCommon>

      <GPT.PromptModalSimple
        visible={visible}
        close={() => toggleVisible()}
        handleConfirm={(data) => {
          const { prompt, ...otherInfo } = data;
          const key = isAdvanced ? 'advancedPrompt' : 'prompt';
          setPromptInfo(
            { ...promptInfo, ...otherInfo, [key]: prompt },
            tab === 'keyword' ? tab : undefined,
          );
          toggleVisible();
        }}
        isAdvanced={isAdvanced}
        title={modalTitle}
        promptList={realPromptList}
        gptType={gptType}
        defaultPromptText={
          isAdvanced ? promptInfo?.advancedPrompt : promptInfo?.prompt
        }
        defaultActive={promptInfo?.prompt ? -1 : 1}
      ></GPT.PromptModalSimple>

      <TimingModal {...urlTipsParams} visible={urlTipsVisible}></TimingModal>
    </div>
  );
}
